import { AppWindow, ToggleLeft, ToggleRight } from 'lucide-react';
import { toast } from 'sonner';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useListNotificationTypesQuery } from '@/client/api/notifications/list-notification-types.query';
import { useUpdateNotificationTypeMutation } from '@/client/api/notifications/update-notification-type.mutation';
import { NotificationType } from '@/client/api/notifications/list-notification-types.query';

const NotificationsInAppPage = () => {
  useBreadcrumbs([
    { label: 'Communications', href: '/admin/communications/notifications' },
    { label: 'Notifications' },
    { label: 'In-app' },
  ]);

  const { data, isLoading, refetch } = useListNotificationTypesQuery();
  const types = data?.data?.notification_types || [];
  const updateMutation = useUpdateNotificationTypeMutation();

  const toggleAllowInApp = (type: NotificationType) => {
    updateMutation.mutate(
      { ntypid: type.ntypid, allow_in_app: !type.allow_in_app },
      {
        onSuccess: () => refetch(),
        onError: (error) => toast.error(error.message || 'Failed to update notification type'),
      }
    );
  };

  const toggleDefaultInApp = (type: NotificationType) => {
    updateMutation.mutate(
      { ntypid: type.ntypid, default_in_app: !type.default_in_app },
      {
        onSuccess: () => refetch(),
        onError: (error) => toast.error(error.message || 'Failed to update notification type'),
      }
    );
  };

  const inAppEnabledCount = types.filter(t => t.allow_in_app).length;
  const defaultOnCount = types.filter(t => t.default_in_app).length;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <AppWindow className="h-7 w-7 text-blue-700" />
          <h1 className="text-2xl font-bold text-gray-900">In-app notifications</h1>
        </div>
        <p className="ml-10 max-w-2xl text-sm text-gray-500">
          Which notification types can appear in the in-app bell feed, and whether they're on by default.
        </p>
      </div>

      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
          <p className="text-sm text-gray-500">Total Types</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">{types.length}</p>
        </div>
        <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
          <p className="text-sm text-gray-500">In-app Enabled</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">{inAppEnabledCount}</p>
        </div>
        <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
          <p className="text-sm text-gray-500">On by Default</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">{defaultOnCount}</p>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Category</th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Available</th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">On by Default</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {isLoading && (
                <tr><td colSpan={4} className="px-6 py-16 text-center text-sm text-gray-500">Loading...</td></tr>
              )}
              {!isLoading && types.length === 0 && (
                <tr><td colSpan={4} className="px-6 py-16 text-center text-sm text-gray-500">No notification types found</td></tr>
              )}
              {types.map((type) => (
                <tr key={type.ntypid} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{type.name}</div>
                    <div className="mt-0.5 text-xs text-gray-500 font-mono">{type.name_system}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 capitalize">{type.category || '—'}</td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => toggleAllowInApp(type)}
                      disabled={updateMutation.isPending}
                      title={type.allow_in_app ? 'Disable in-app channel' : 'Enable in-app channel'}
                      className="disabled:opacity-50"
                    >
                      {type.allow_in_app
                        ? <ToggleRight className="w-6 h-6 text-green-500 ml-auto" />
                        : <ToggleLeft className="w-6 h-6 text-gray-400 ml-auto" />}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => toggleDefaultInApp(type)}
                      disabled={updateMutation.isPending || !type.allow_in_app}
                      title={!type.allow_in_app ? 'Enable the channel first' : type.default_in_app ? 'Turn off by default' : 'Turn on by default'}
                      className="disabled:opacity-30"
                    >
                      {type.default_in_app
                        ? <ToggleRight className="w-6 h-6 text-green-500 ml-auto" />
                        : <ToggleLeft className="w-6 h-6 text-gray-400 ml-auto" />}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default NotificationsInAppPage;
